import { esES } from '@mui/x-data-grid/locales'

/**
 * Treasury-specific Spanish localization for MUI X DataGrid
 * Extends the default Spanish locale with treasury domain terms
 */
export const treasurySpanishLocale = {
  ...esES,
  
  // Toolbar customizations
  toolbarExport: 'Exportar',
  toolbarExportLabel: 'Exportar datos',
  toolbarExportCSV: 'Descargar como CSV',
  toolbarExportPrint: 'Imprimir tabla',
  toolbarColumns: 'Columnas',
  toolbarColumnsLabel: 'Seleccionar columnas',
  toolbarFilters: 'Filtros',
  toolbarFiltersLabel: 'Mostrar filtros',
  toolbarFiltersTooltipHide: 'Ocultar filtros',
  toolbarFiltersTooltipShow: 'Mostrar filtros',
  toolbarFiltersTooltipActive: (count: number) =>
    count !== 1 ? `${count} filtros activos` : `${count} filtro activo`,
  toolbarQuickFilterPlaceholder: 'Buscar en todos los campos...',
  toolbarQuickFilterLabel: 'Búsqueda rápida',
  toolbarQuickFilterDeleteIconLabel: 'Limpiar búsqueda',
  
  // Search result messages
  footerRowSelected: (count: number) =>
    count !== 1 ? `${count} registros seleccionados` : `${count} registro seleccionado`,
  toolbarDensity: 'Densidad',
  toolbarDensityLabel: 'Ajustar densidad de filas',
  toolbarDensityCompact: 'Compacta',
  toolbarDensityStandard: 'Estándar',
  toolbarDensityComfortable: 'Cómoda',
  
  // Column menu
  columnMenuLabel: 'Menú de columna',
  columnMenuShowColumns: 'Mostrar columnas',
  columnMenuManageColumns: 'Administrar columnas',
  columnMenuFilter: 'Filtrar',
  columnMenuHideColumn: 'Ocultar columna',
  columnMenuUnsort: 'Quitar ordenamiento',
  columnMenuSortAsc: 'Ordenar ascendente',
  columnMenuSortDesc: 'Ordenar descendente',
  
  // Pagination
  paginationRowsPerPageLabel: 'Registros por página:',
  paginationLabelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
    `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`,
  paginationLabelRowsPerPage: 'Registros por página:',
  
  // Filter panel
  filterPanelAddFilter: 'Agregar filtro',
  filterPanelRemoveAll: 'Quitar todos',
  filterPanelDeleteIconLabel: 'Eliminar filtro',
  filterPanelLinkOperator: 'Operador lógico',
  filterPanelOperators: 'Operador',
  filterPanelOperatorAnd: 'Y',
  filterPanelOperatorOr: 'O',
  filterPanelColumns: 'Campo',
  filterPanelInputLabel: 'Valor',
  filterPanelInputPlaceholder: 'Ingrese el valor a filtrar',
  
  // Filter operators for text fields
  filterOperatorContains: 'contiene',
  filterOperatorEquals: 'es igual a',
  filterOperatorStartsWith: 'comienza con',
  filterOperatorEndsWith: 'termina con',
  filterOperatorIs: 'es',
  filterOperatorNot: 'no es',
  filterOperatorIsEmpty: 'está vacío',
  filterOperatorIsNotEmpty: 'no está vacío',
  filterOperatorIsAnyOf: 'es cualquiera de',
  
  // Filter operators for date fields
  filterOperatorAfter: 'después de',
  filterOperatorOnOrAfter: 'en o después de',
  filterOperatorBefore: 'antes de',
  filterOperatorOnOrBefore: 'en o antes de',
  
  // Filter operators for number fields  
  filterOperator: '=',
  filterOperatorGreaterThan: 'mayor que',
  filterOperatorGreaterThanOrEqual: 'mayor o igual que',
  filterOperatorLessThan: 'menor que',
  filterOperatorLessThanOrEqual: 'menor o igual que',
  
  // Column visibility
  columnsManagementSearchTitle: 'Buscar columnas',
  columnsManagementNoColumns: 'No hay columnas',
  columnsManagementShowHideAllText: 'Mostrar/Ocultar todas',
  columnsManagementReset: 'Restablecer',
  
  // Row selection
  checkboxSelectionHeaderName: 'Selección',
  checkboxSelectionSelectAllRows: 'Seleccionar todas las filas',
  checkboxSelectionUnselectAllRows: 'Deseleccionar todas las filas',
  checkboxSelectionSelectRow: 'Seleccionar fila',
  checkboxSelectionUnselectRow: 'Deseleccionar fila',
  
  // Treasury-specific states and messages
  noRowsLabel: 'No hay registros financieros para mostrar',
  noResultsOverlayLabel: 'No se encontraron resultados que coincidan con los filtros aplicados',
  errorOverlayDefaultLabel: 'Ocurrió un error al cargar los datos financieros',
  loadingOverlayLabel: 'Cargando datos...',
  
  // Row grouping (if used)
  groupingColumnHeaderName: 'Agrupar',
  groupByColumn: (column: string) => `Agrupar por ${column}`,
  unGroupByColumn: (column: string) => `Desagrupar ${column}`,
  
  // Column resizing
  columnHeaderSortIconLabel: 'Ordenar',
  columnHeaderFiltersTooltipActive: (count: number) =>
    count !== 1 ? `${count} filtros activos` : `1 filtro activo`,
  columnHeaderFiltersLabel: 'Mostrar filtros',
  columnHeaderSortDesc: 'Orden descendente',
  columnHeaderSortAsc: 'Orden ascendente',
}

/**
 * Common Spanish labels for treasury application components
 */
export const treasuryLabels = {
  // Common actions
  actions: {
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    view: 'Ver',
    export: 'Exportar',
    print: 'Imprimir',
    refresh: 'Actualizar',
    search: 'Buscar',
    filter: 'Filtrar',
    clear: 'Limpiar',
    confirm: 'Confirmar',
    back: 'Volver',
    next: 'Siguiente',
    previous: 'Anterior',
    close: 'Cerrar',
  },
  
  // Financial terms
  financial: {
    amount: 'Monto',
    value: 'Valor',
    balance: 'Saldo',
    total: 'Total',
    subtotal: 'Subtotal',
    invoice: 'Factura',
    payment: 'Pago',
    date: 'Fecha',
    dueDate: 'Fecha de vencimiento',
    provider: 'Proveedor',
    project: 'Proyecto',
    account: 'Cuenta',
    bank: 'Banco',
    document: 'Documento',
    observation: 'Observación',
    status: 'Estado',
    currency: 'Moneda',
  },
  
  // Status indicators
  status: {
    pending: 'Pendiente',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    paid: 'Pagado',
    overdue: 'Vencido',
    processing: 'Procesando',
    completed: 'Completado',
    cancelled: 'Cancelado',
  },
  
  // Messages
  messages: {
    loading: 'Cargando...',
    saving: 'Guardando...',
    noData: 'No hay datos disponibles',
    error: 'Ha ocurrido un error',
    success: 'Operación completada exitosamente',
    confirmDelete: '¿Está seguro de que desea eliminar este registro?',
    unsavedChanges: 'Tiene cambios sin guardar. ¿Desea continuar?',
  },
  
  // Validation messages
  validation: {
    required: 'Este campo es obligatorio',
    invalidFormat: 'Formato inválido',
    invalidEmail: 'Correo electrónico inválido',
    invalidDate: 'Fecha inválida',
    invalidAmount: 'Monto inválido',
    minLength: (min: number) => `Mínimo ${min} caracteres`,
    maxLength: (max: number) => `Máximo ${max} caracteres`,
  },
}

export default treasurySpanishLocale